import { useEffect, useMemo, useState } from 'react';
import { HOTEL_INVENTORY_CAP } from '@voll-intelligence/domain';
import {
  DEFAULT_HOTEL_RESULTS_ORDER,
  EMPTY_HOTEL_RESULTS_FILTERS,
  soldOutView,
  withoutSoldOutRate,
  type HotelOfferView,
  type HotelResultsFilterValue,
  type HotelResultsOrder,
} from '@voll-intelligence/intelligence-ui-kit';
import type {
  ChatUiHotelOffer,
  HotelPlannerPlace,
  HotelPlannerDirectives,
  HotelPlannerPort,
} from '@voll-intelligence/genui-contract';
import { directiveFiltersValue } from '../directive_panel_filters.js';
import { hotelOfferViews } from '../../shared/hotel_offer_view.js';
import { genUiT } from '../../../shared/ui/i18n.js';
import { shownHotelViews } from './hotel_results_filter_view.js';
import {
  matchesStayPreferences,
  panelSourceOffers,
  panelTotal,
  toHotelResultsCuts,
  toHotelResultsOrder,
} from './hotel_results_query.js';
import { useHotelResultsPage } from './use_hotel_results_page.js';

export type HotelPanelList = {
  readonly page: ReturnType<typeof useHotelResultsPage>;
  /** As linhas CARREGADAS (a página, ou o bloco enquanto ela não volta) — a
   *  lista do ui-kit recebe estas e aplica o corte de cliente sozinha. */
  readonly views: readonly HotelOfferView[];
  /** As linhas que a TELA mostra: `views` depois do corte de cliente e da
   *  ordem (`shownHotelViews`). É o que o relato ao agente conta (#2857) —
   *  `views` inclui o que o filtro escondeu. */
  readonly visibleViews: readonly HotelOfferView[];
  /** As mesmas ofertas em forma de fato, para os cortes e as facetas. */
  readonly sources: readonly ChatUiHotelOffer[];
  /** Quantos hotéis o conjunto inteiro tem, quando dá para afirmar. */
  readonly total: number | undefined;
  /** A moeda da lista — a régua do corte de preço e do rótulo dele. Sai das
   *  ofertas em cena, então acompanha a lista e não o bloco. */
  readonly currency: string;
  /** O par filtros+ordem corrente. Dono ÚNICO do estado: a lista cheia e o mapa
   *  cheio são CONTROLADOS por ele — é o que faz o corte viajar entre as duas
   *  superfícies e sobreviver a fechar/reabrir enquanto o organismo vive. */
  readonly query: HotelPanelQuery;
  readonly setQuery: (query: HotelPanelQuery) => void;
};

export type HotelPanelQuery = {
  readonly filters: HotelResultsFilterValue;
  readonly order: HotelResultsOrder;
};

export const EMPTY_HOTEL_PANEL_QUERY: HotelPanelQuery = {
  filters: EMPTY_HOTEL_RESULTS_FILTERS,
  order: DEFAULT_HOTEL_RESULTS_ORDER,
};

/** M1 · 1.2 — as diretivas do bloco semeiam o painel: filtro/ordem do agente
 *  viram o estado inicial (e o reset de busca nova) da lista completa. Pisos
 *  de nota são os do ui-kit (`HOTEL_RATING_BAND_FLOORS`) a partir do menor
 *  piso pedido. */
export function directivesToPanelQuery(
  directives: HotelPlannerDirectives | undefined,
): HotelPanelQuery {
  if (!directives) return EMPTY_HOTEL_PANEL_QUERY;
  return {
    order: directives.sortBy ?? DEFAULT_HOTEL_RESULTS_ORDER,
    filters: directiveFiltersValue(directives.filter),
  };
}

/** Café, reembolso, comodidade e piso de diária só existem no CLIENTE: o
 *  fornecedor não os corta (`toHotelResultsCuts`), nem as preferências de
 *  estadia (`matchesStayPreferences` sobre `sources`), então o recorte só é
 *  contável sobre a lista inteira. */
function hasClientOnlyCut(
  filters: HotelResultsFilterValue,
  stay: {
    readonly onlyBreakfast: boolean | undefined;
    readonly onlyRefundable: boolean | undefined;
  },
): boolean {
  return (
    filters.breakfastOnly ||
    filters.refundableOnly ||
    filters.amenityIds.length > 0 ||
    filters.minimumNightlyPrice !== undefined ||
    stay.onlyBreakfast === true ||
    stay.onlyRefundable === true
  );
}

/**
 * #3785: com corte só do cliente ligado, o painel lê as páginas seguintes
 * sozinho até o fim da busca (no teto do inventário, o mesmo que o agente lê em
 * `_filter_pages.ts`). Sem isto o cabeçalho contava os que passam entre os 20
 * carregados sobre o total SEM corte — "17 hotéis de 74" enquanto o agente,
 * sobre as 74, dizia 60 com café.
 */
function useClientCutDrain(page: ReturnType<typeof useHotelResultsPage>, active: boolean): void {
  const { phase, hasMore, loadMore, offers } = page;
  const loaded = offers.length;
  useEffect(() => {
    if (active && phase === 'ready' && hasMore && loaded < HOTEL_INVENTORY_CAP) loadMore();
  }, [active, phase, hasMore, loaded, loadMore]);
}

/**
 * A lista COMPLETA do planner: as páginas lidas e as vistas que saem delas.
 *
 * Mora acima das duas superfícies que a mostram — a lista cheia e o mapa em tela
 * cheia — porque elas são a MESMA lista vista de dois jeitos, e o mapa abre por
 * cima da lista sem desmontá-la. Enquanto isto vivia dentro do painel, o mapa
 * cheio só alcançava as ofertas do bloco (6) e contradizia a lista aberta atrás
 * dele.
 *
 * Enquanto a primeira página não volta (ou não há porta de paginação), a fonte é
 * o que o bloco trouxe — a lista RESUMIDA da busca. Assim que a página chega ela
 * ESTENDE aquela sem corte e a substitui com corte (`panelSourceOffers`, #3727),
 * e as vistas passam a ser construídas aqui em vez de reaproveitar as do card.
 */
export function useHotelPanelList(input: {
  readonly port: HotelPlannerPort | undefined;
  readonly sessionId: string | undefined;
  /** Alguma das duas superfícies está aberta? Fechadas não buscam. */
  readonly enabled: boolean;
  readonly blockViews: readonly HotelOfferView[];
  readonly blockOffers: readonly ChatUiHotelOffer[];
  // Uniões explícitas em vez de campos opcionais: com
  // `exactOptionalPropertyTypes`, opcional obrigaria o chamador a espalhar cada
  // um condicionalmente, e são esses spreads que estufam a complexidade do
  // componente sem dizer nada.
  readonly stayPreferences: {
    readonly onlyBreakfast: boolean | undefined;
    readonly onlyRefundable: boolean | undefined;
  };
  readonly nights: number | undefined;
  readonly place: HotelPlannerPlace | undefined;
  readonly locale: string;
  readonly proxyImageUrl: (url: string) => string;
  readonly detailsAvailable: boolean;
  /** M1 · 1.2 — o estado inicial (e o de reset por busca nova) do painel: as
   *  diretivas do bloco quando o agente as trouxe, `EMPTY_HOTEL_PANEL_QUERY`
   *  senão (`directivesToPanelQuery` decide). */
  readonly initialQuery: HotelPanelQuery;
  /** O conjunto novo refina a MESMA pergunta — a área do mapa (#3984): mesmas
   *  datas e ocupação, a pessoa só moveu o mapa. Aí o corte segue de pé. */
  readonly keepQuery: boolean;
  /** Hotéis cujo detalhe já voltou sem quarto (#4469, `useSoldOutOfferIds`). */
  readonly soldOut: ReadonlySet<string>;
}): HotelPanelList {
  const { blockViews, blockOffers, nights, place, initialQuery, keepQuery } = input;
  // Os handles do bloco são a impressão digital da busca: cada busca emite
  // handles novos, inclusive quando cai na mesma sessão. É o que faz o painel
  // recomeçar sozinho depois de um "Buscar de novo".
  const resetToken = blockOffers.map(({ offerId }) => offerId).join('|');
  const [query, setQuery] = useState<HotelPanelQuery>(initialQuery);
  // Busca nova = conjunto novo, filtros do zero: um teto de diária da cidade
  // anterior esvaziaria a lista nova sem explicação. Reset no RENDER (e não em
  // efeito) para a página 1 do conjunto novo já sair sem os cortes velhos.
  const [queriedToken, setQueriedToken] = useState(resetToken);
  if (queriedToken !== resetToken) {
    setQueriedToken(resetToken);
    if (!keepQuery) setQuery(initialQuery);
  }
  const pageQuery = useMemo(() => {
    const cuts = toHotelResultsCuts(query.filters);
    return { ...(cuts ? { cuts } : {}), order: toHotelResultsOrder(query.order) };
  }, [query]);
  const page = useHotelResultsPage({
    port: input.port,
    sessionId: input.sessionId,
    enabled: input.enabled,
    query: pageQuery,
    resetToken,
  });
  useClientCutDrain(page, input.enabled && hasClientOnlyCut(query.filters, input.stayPreferences));
  // O host monta este objeto inline, então ele chega com identidade nova a cada
  // render. Como ele é dependência do filtro e das vistas — que percorrem a
  // lista ACUMULADA —, estabilizá-lo aqui é o que impede a página inteira de
  // ser reformatada a cada tecla do painel. `undefined` quando NADA foi pedido:
  // é o que diz a `panelTotal` que o total do fornecedor ainda vale.
  const onlyBreakfast = input.stayPreferences.onlyBreakfast;
  const onlyRefundable = input.stayPreferences.onlyRefundable;
  const stayPreferences = useMemo(
    () =>
      onlyBreakfast === undefined && onlyRefundable === undefined
        ? undefined
        : {
            ...(onlyBreakfast === undefined ? {} : { onlyBreakfast }),
            ...(onlyRefundable === undefined ? {} : { onlyRefundable }),
          },
    [onlyBreakfast, onlyRefundable],
  );
  const { paged, offers: listed } = panelSourceOffers(
    page,
    blockOffers,
    pageQuery.cuts !== undefined,
  );
  const { soldOut } = input;
  const source = useMemo(
    () =>
      soldOut.size === 0
        ? listed
        : listed.map((offer) => (soldOut.has(offer.offerId) ? withoutSoldOutRate(offer) : offer)),
    [listed, soldOut],
  );
  const sources = useMemo(
    () =>
      stayPreferences
        ? source.filter((offer) => matchesStayPreferences(offer, stayPreferences))
        : source,
    [source, stayPreferences],
  );
  const views = useMemo(
    () =>
      (paged
        ? hotelOfferViews(sources, {
            locale: input.locale,
            proxyImageUrl: input.proxyImageUrl,
            detailsAvailable: input.detailsAvailable,
            ...(nights === undefined ? {} : { nights }),
            ...(place ? { place } : {}),
          })
        : blockViews.filter((view) => sources.some((offer) => offer.offerId === view.id))
      ).map((view) =>
        soldOut.has(view.id) ? soldOutView(view, genUiT(input.locale, 'hotel.soldOut')) : view,
      ),
    [
      blockViews,
      input.detailsAvailable,
      input.locale,
      input.proxyImageUrl,
      nights,
      paged,
      place,
      soldOut,
      sources,
    ],
  );
  const currency = sources.find(({ price }) => price)?.price?.currency ?? 'BRL';
  const visibleViews = useMemo(
    () => shownHotelViews(views, sources, query.filters, query.order, { nights, place, currency }),
    [currency, nights, place, query, sources, views],
  );
  return {
    page,
    views,
    visibleViews,
    sources,
    total: panelTotal(page.total, stayPreferences, views.length),
    currency,
    query,
    setQuery,
  };
}
